import axios from 'axios';
import type { ExecutionLog, ExecutionLogsResponse, ExecutionLogStatsResponse, TriggerType } from '../types/executionLog';

interface GetLogsParams {
  serviceId: string;
  limit?: number;
  offset?: number;
  triggerType?: TriggerType;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  metadata?: {
    total: number;
    limit: number;
    offset: number;
  };
}

class ExecutionLogService {
  async getLogs({ serviceId, limit = 50, offset = 0, triggerType }: GetLogsParams): Promise<ExecutionLogsResponse> {
    try {
      const params: Record<string, string> = {
        limit: String(limit),
        offset: String(offset)
      };

      if (triggerType) {
        params.trigger_type = triggerType;
      }

      const response = await axios.get<ApiResponse<ExecutionLog[]>>(
        `/api/services/${serviceId}/logs`,
        { params }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Erro ao buscar logs');
      }

      return {
        logs: response.data.data || [],
        total: response.data.metadata?.total || 0,
        limit: response.data.metadata?.limit || limit,
        offset: response.data.metadata?.offset || offset
      };
    } catch (error) {
      console.error('Error fetching logs:', error);
      throw new Error('Erro ao carregar historico de execucoes');
    }
  }

  async getLogById(serviceId: string, logId: string): Promise<ExecutionLog> {
    try {
      const response = await axios.get<ApiResponse<ExecutionLog>>(
        `/api/services/${serviceId}/logs/${logId}`
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Erro ao buscar log');
      }

      if (!response.data.data) {
        throw new Error('Log não encontrado');
      }

      return response.data.data;
    } catch (error) {
      console.error('Error fetching log:', error);
      throw new Error('Erro ao carregar detalhes do log');
    }
  }

  async getStats(serviceId: string, period: '1h' | '24h' | '7d' | '30d' = '24h'): Promise<ExecutionLogStatsResponse> {
    try {
      const response = await axios.get<ApiResponse<ExecutionLogStatsResponse>>(
        `/api/services/${serviceId}/logs/stats`,
        { params: { period } }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Erro ao buscar estatisticas');
      }

      if (!response.data.data) {
        throw new Error('Dados não encontrados');
      }

      return response.data.data;
    } catch (error) {
      console.error('Error fetching stats:', error);
      throw new Error('Erro ao carregar estatisticas');
    }
  }

  getTriggerTypeLabel(type: TriggerType): string {
    const labels: Record<TriggerType, string> = {
      manual: 'Manual',
      api: 'API',
      webhook: 'Webhook',
      solace: 'Solace',
      sap_btp: 'SAP BTP'
    };
    return labels[type] || type;
  }

  getTriggerTypeColor(type: TriggerType): string {
    const colors: Record<TriggerType, string> = {
      manual: 'bg-purple-100 text-purple-700',
      api: 'bg-blue-100 text-blue-700',
      webhook: 'bg-amber-100 text-amber-700',
      solace: 'bg-emerald-100 text-emerald-700',
      sap_btp: 'bg-cyan-100 text-cyan-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  }

  formatExecutionTime(ms: number): string {
    if (ms < 1000) {
      return `${ms}ms`;
    }
    return `${(ms / 1000).toFixed(2)}s`;
  }

  formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }
}

export const executionLogService = new ExecutionLogService();
